import React, { useEffect, useState } from "react";
import { Card, Form } from "antd";
import { FaArrowLeft, FaCircleCheck } from "react-icons/fa6";
import { useDispatch, useSelector } from "react-redux";
import EmployeeProgressCard from "./EmployeeProgressCard";
import EmployeeScoreCard from "./EmployeeScoreCard";
import HeaderComponent from "components/card/HeaderComponent";
import ReviewScore from "./ReviewScore";
import WrittenFeedback from "./WrittenFeedback";
import RatingReference from "./RatingReference";
import { Users, Target ,TrendingUp, CircleCheck } from "lucide-react";
import {
  doPerfReviewUpdate,
  getPerfReviewDetails
} from "../../../../../redux/performancereview/performanceReviewState"
import {
  getMonthlyPerfReviewItems
} from "../../../../../redux/performancereviewitem/performanceReviewItemState";
import { useNavigate } from "react-router-dom";
import ButtonWithConfirm from "components/button/buttonwithconfirm";
const StartReviewForm = ({ recordId, mode }) => {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [ratings, setRatings] = useState({});
  const reviewItems = useSelector((state) => state.performanceReviewItem.monthlyPerfReviewItems.items);
  const record = useSelector((state) => state.performanceReview.perfReviewDetails.data);
  const updateSuccess = useSelector((state) => state.performanceReview.perfReviewDetails.updateSuccess);
  const viewMode = mode === "view"; 
  const [form] = Form.useForm();
  useEffect(() => {
      dispatch(getPerfReviewDetails(recordId));
  }, [dispatch, recordId]);

  useEffect(() => {
    if (!viewMode || !record?.pfr_result?.sections) return;

    const savedRatings = {};

    record.pfr_result.sections.forEach(section => {
      section.categories.forEach(category => {
        savedRatings[category.pri_id] = category.rating;
      });
    });

    setRatings(savedRatings);
  }, [record, viewMode]);

  const handleRatingChange = (categoryId, value) => {
    if (viewMode) return;

    form.setFieldValue(["overall_rating", categoryId], value);

    setRatings(prev => ({
      ...prev,
      [categoryId]: value,
    }));
  };

  const pointsMap = { 1: 25, 2: 50, 3: 75, 4: 100 };
  const earnedPoints = Object.values(ratings).reduce(
    (sum, rating) => sum + (pointsMap[rating] || 0),
    0
  );

  useEffect(() => {
      dispatch(getMonthlyPerfReviewItems({for_5th_month: 1}));
  },[dispatch]);
  
  const completed = Object.keys(ratings).length;
  const total = 8;
  const  grandTotal = total * 100;
  const thresholds = [
    { label: "Exceeds", min: grandTotal - 125 + 1 },
    { label: "Meets", min: grandTotal - 250 + 1 },
    { label: "Needs improvement", min: grandTotal - 375 + 1 },
    { label: "Does not meet expectations", min: 0 },
  ];

  const overallResult = thresholds.find(t => earnedPoints >= t.min)?.label || null;

  const reviewSections = reviewItems
    .filter(item => item.pri_parent_id === null)
    .map(section => ({
      ...section,
      categories: reviewItems
        .filter(category => category.pri_parent_id === section.pri_id)
        .map(category => ({
          ...category,
          ratings: reviewItems.filter(
            rating => rating.pri_parent_id === category.pri_id
          )
        }))
    }));

  const handleSubmit = (values) => {
    if (completed !== total) return;

    const pfr_result = {};

      pfr_result.sections = reviewSections.map((section) => ({
        pri_id: section.pri_id,
        categories: section.categories.map((category) => ({
          pri_id: category.pri_id,
          rating: ratings[category.pri_id] ?? null,
      })),
    }));

    pfr_result.strengths = values.strengths;
    pfr_result.improvements = values.improvements;
    pfr_result.goals = values.goals;
    pfr_result.comments = values.comments;
    pfr_result.overall_result = overallResult;

    dispatch(doPerfReviewUpdate({
      pfr_id: recordId,
      pfr_result,
    }));
  };

  useEffect(() => {
    if (updateSuccess) {
      navigate("/console/performance-reviews");
    }
  }, [updateSuccess, navigate]);

  const sectionStyleMap = {
    "Our People": {
      borderClass: "border-emerald-200 dark:border-emerald-700",
      icon: <Users size={20} />,
      colorTheme: "dark-green",
      bgcolor: "bg-emerald-50 dark:bg-emerald-900",
      hoverBorderClass: "hover:border-emerald-400",
      ringClass: "ring-emerald-300",
    },
    "Our Services": {
      borderClass: "border-blue-200 dark:border-blue-700",
      icon: <Target size={20} />,
      colorTheme: "dark-blue",
      bgcolor: "bg-blue-50 dark:bg-blue-900",
      hoverBorderClass: "hover:border-blue-400",
      ringClass: "ring-blue-400",
    },
    "Our Growth": {
      borderClass: "border-purple-200 dark:border-purple-700",
      icon: <TrendingUp size={20} />,
      colorTheme: "dark-purple",
      bgcolor: "bg-purple-50 dark:bg-purple-900",
      hoverBorderClass: "hover:border-purple-400",
      ringClass: "ring-purple-300",
    },
  };

  return (
    <Card className="min-h-screen gradient pink  dark:from-intsamis-900 dark:to-purple-900">
      <div className=" mx-auto p-4 md:p-6 lg:p-8 space-y-6">

        <div
          className="inline-flex items-center gap-2 px-3 title-text cursor-pointer"
          onClick={() =>
            navigate(
              mode === "view"
                ? "/console/performance-reviews#review-history"
                : "/console/performance-reviews"
            )
          }
        >
          <FaArrowLeft />
          Back to Reviews
        </div>

        <EmployeeProgressCard
          record={record}
          completed={completed}
          total={total}
        />

        {completed === total && (
          <EmployeeScoreCard overallResult={overallResult} />
        )}
        <RatingReference/>

        <Form
          form={form}
          onFinish={handleSubmit}
          layout="vertical"
          className="space-y-6"
        >
          {reviewSections.map((section) => {
              const style = sectionStyleMap[section.pri_title] || {};
              return (
                <div key={section.pri_id} className={`border-2 rounded-2xl ${style.borderClass}`}>
                  <HeaderComponent
                    title={section.pri_title}
                    titleClass="text-lg font-semibold"
                    description={section.pri_description}
                    descriptionClass="text-base muted-text"
                    bgcolor={style.bgcolor}
                    icon={style.icon}
                    colorTheme={style.colorTheme}
                    cardBorder="border-none"
                    mode={mode}
                  >
                    {section.categories.map((category) => {
                      const items = category.ratings.map((rating) => ({
                        rate: rating.stars,
                        scale: rating.pri_title.replace(/^\d+\s*/, ""), 
                        points: pointsMap[rating.stars],
                        description: rating.pri_description,
                      }));

                      const scaleMap = {
                        "Does not meet expectations": "Needs Focus",
                        "Needs Improvement": "Developing",
                        "Meets": "Meets",
                        "Exceeds": "Exceeds",
                      };

                      return (
                        <ReviewScore
                          key={category.pri_id}
                          categoryId={category.pri_id}
                          category={category.pri_title}
                          borderClass={style.borderClass}
                          hoverBorderClass={style.hoverBorderClass}
                          ringClass={style.ringClass}
                          items={items}
                          scaleMap={scaleMap}
                          value={ratings[category.pri_id] || null}
                          onChange={(value) => handleRatingChange(category.pri_id, value)}
                          mode={mode}
                        />
                      );
                    })}
                  </HeaderComponent>
                </div>
              );
            })}

          <div className="border-2 rounded-xl border-slate-200 dark-mode-card">
            <HeaderComponent
              title="Written Feedback"
              titleClass="text-lg font-semibold"
              description="Provide detailed feedback and set goals for the next period"
              descriptionClass="text-base muted-text"
              cardBorder="border-none"
            >
              <div className="dark:bg-intsamis-900 p-3 rounded-xl">
                <WrittenFeedback  
                  mode={mode}
                  recordData={record}
                />
              </div>
            </HeaderComponent>
          </div>
        </Form>

        {!viewMode && (
          <Card className="gap-6 rounded-xl border-2 border-purple-200 shadow-lg dark-mode-card">
            <div className="flex justify-between items-center">
              <p className="text-[13px] muted-text">Please complete all sections before submitting</p>
              <div className="flex gap-2">
                <button
                  className="flex items-center border-2 rounded-xl bg-white text-gray-800 p-2 text-sm h-9"
                  onClick={() => navigate(`/console/performance-reviews`)} 
                >
                    Cancel
                </button>
              
                <ButtonWithConfirm
                  className={`
                    text-sm rounded-xl pb-2 flex items-center h-9
                    ${
                      completed === total
                        ? "btn-gradient-emerald hover:text-white"
                        : "btn-gradient-emerald opacity-50 cursor-not-allowed"
                    }
                  `}
                  confirmLabel="Submit"
                  disabled={completed !== total}
                  confirmationTitle="Submit Review?"
                  confirmationMessage="Are you sure you want to submit this review?"
                  confirmHandler={() => form.submit()}
                >
                  <CircleCheck className="w-4 h-4 mr-2"/> Submit Review
                </ButtonWithConfirm>
              </div>
            </div>
          </Card>
        )}
      </div>
    </Card>
  );
};

export default (StartReviewForm);
